import { useMemo, useState } from 'react'
import {
  AppBar, Box, Dialog, IconButton, Toolbar, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'
import { selectAdminConfigs } from '../../reducers/Beacon4PerformanceAdminReducer'
import Beacon4PerformanceAdminKpiList from './Beacon4PerformanceAdminKpiList'
import Beacon4PerformanceAdminKpiDetail from './Beacon4PerformanceAdminKpiDetail'
import Beacon4PerformanceAdminMetadataDialog from './Beacon4PerformanceAdminMetadataDialog'

/**
 * Manage KPIs — master/detail layout (list on the left, KPI detail on the
 * right). The selection drives a single context, so every sub-tab
 * (Overview / Formula / Breakdowns / Audit) is scoped to the same KPI.
 */
export default function Beacon4PerformanceAdminDialog({ open, onClose }) {
  const configs = useSelector(selectAdminConfigs)

  const [selectedId, setSelectedId] = useState(null)
  const [metaOpen, setMetaOpen]     = useState(false)
  const [metaConfig, setMetaConfig] = useState(null) // null = create/clone
  const [cloneSource, setCloneSource] = useState(null) // set only for clone

  // Derive the selected config from the store so the detail pane stays fresh
  // after edits / refetches.
  const selectedConfig = useMemo(
    () => configs.find(c => c.id === selectedId) ?? null,
    [configs, selectedId]
  )

  const openCreate = () => { setMetaConfig(null); setCloneSource(null); setMetaOpen(true) }
  const openEdit   = (cfg) => { setMetaConfig(cfg); setCloneSource(null); setMetaOpen(true) }
  const openClone  = (cfg) => { setMetaConfig(null); setCloneSource(cfg); setMetaOpen(true) }
  const closeMeta  = () => { setMetaOpen(false); setCloneSource(null) }

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="relative" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar variant="dense">
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
            Performance — Manage KPIs
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', bgcolor: 'background.default' }}>
        <Beacon4PerformanceAdminKpiList
          selectedId={selectedId}
          onSelect={setSelectedId}
          onNew={openCreate}
        />
        <Beacon4PerformanceAdminKpiDetail
          config={selectedConfig}
          onEditMetadata={openEdit}
          onClone={openClone}
        />
      </Box>

      <Beacon4PerformanceAdminMetadataDialog
        open={metaOpen}
        config={metaConfig}
        cloneSource={cloneSource}
        onClose={closeMeta}
      />
    </Dialog>
  )
}